import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'
import {
  ProjectsDataTableDocument,
  ProjectsDataTableQuery,
} from 'generated/resources/datatable.graphql'

import { useDataTable } from 'modules/core/components/DataTable'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import Upgrade from 'modules/core/components/Upgrade'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import BulkAssignProjectClientModal from 'modules/project_management/components/modals/BulkAssignProjectClientModal'
import BulkAssignProjectRolesModal from 'modules/project_management/components/modals/BulkAssignProjectRolesModal'
import { UpsellProjectsBulkActionsModal } from 'modules/project_management/components/upsells/UpsellProjectsBulkActionsModal'

const Actions = ({
  canCreateProjects,
  hasBulkActions,
  hasClientsFeature,
  onClickCreateProject,
}: {
  canCreateProjects: boolean
  hasBulkActions: boolean
  hasClientsFeature: boolean
  onClickCreateProject: () => void
}) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [showBulkAssigProjectRolesModal, setShowBulkAssigProjectRolesModal] = useState(false)
  const [showBulkAssigProjectClientModal, setShowBulkAssigProjectClientModal] = useState(false)

  const [showUpgradeModal, setShowUpgradeModal] = useState(false)

  const onCloseUpsellModal = () => {
    setShowUpgradeModal(false)
  }

  const dataTable = useDataTable<ProjectsDataTableQuery>()

  const canAssignEmployees = dataTable.data
    .map((item) => item._policies_assignUnassignProjectWorkers.can)
    .some((can) => can)

  const showActions = (canAssignEmployees || hasBulkActions) && dataTable.selectedRows.size != 0
  const projectIdWithPermissions = dataTable.data.map((item) => {
    return {
      id: item.id,
      canAssignOwners: item._policies_assignOwner.can,
    }
  })

  const closeBulkAssigProjectRolesModal = () => {
    setShowBulkAssigProjectRolesModal(false)
  }

  const bulkAssignRolesAction = () => {
    if (hasBulkActions) {
      track(trackingEvents.projectManagement.bulkActions.roleProjectsAssignment.eventStart)
      setShowBulkAssigProjectRolesModal(true)
    } else {
      track(trackingEvents.projectManagement.bulkActions.upgrade.visitUpgradeModal)
      setShowUpgradeModal(true)
    }
  }

  return (
    <>
      {canCreateProjects && (
        <Button iconName="add" type="primary" onClick={onClickCreateProject}>
          {i18n.t('project_management.add_button')}
        </Button>
      )}
      {showActions && (
        <PopoverMenu type="list">
          <PopoverMenu.Trigger>
            <Button type="secondary">{i18n.t('common.actions')}</Button>
          </PopoverMenu.Trigger>
          <PopoverMenu.Content side="bottom" align="end">
            {canAssignEmployees && (
              <PopoverMenu.TouchableOption
                title={i18n.t('project_management.table.projects_list.menu_links.assign_employees')}
                onClick={() => {
                  track(
                    trackingEvents.projectManagement.bulkActions.employeeProjectsAssignment
                      .eventStart
                  )
                  navigate(navigation.projects.list.workers.fullPath(), {
                    state: { hasBulkActions },
                  })
                }}
              />
            )}
            {
              <PopoverMenu.TouchableOption
                title={i18n.t('project_management.table.projects_list.menu_links.assign_roles')}
                onClick={bulkAssignRolesAction}
                extraContent={!hasBulkActions ? <Upgrade.Tiny /> : null}
              />
            }
            {hasClientsFeature && (
              <PopoverMenu.TouchableOption
                title={i18n.t('project_management.table.projects_list.menu_links.assign_client')}
                onClick={() => {
                  track(trackingEvents.projectManagement.bulkActions.clientAssignment.eventStart)
                  setShowBulkAssigProjectClientModal(true)
                }}
              />
            )}
          </PopoverMenu.Content>
        </PopoverMenu>
      )}
      {showBulkAssigProjectRolesModal && (
        <BulkAssignProjectRolesModal
          projectIdWithPermissions={projectIdWithPermissions}
          projectIds={Array.from(dataTable.selectedRows).map((item) => +item)}
          onClose={closeBulkAssigProjectRolesModal}
        />
      )}
      {showUpgradeModal && <UpsellProjectsBulkActionsModal onClose={onCloseUpsellModal} />}
      {showBulkAssigProjectClientModal && (
        <BulkAssignProjectClientModal
          projectIds={Array.from(dataTable.selectedRows).map((item) => +item)}
          onClose={() => {
            setShowBulkAssigProjectClientModal(false)
          }}
          refetchQueries={[ProjectsDataTableDocument]}
        />
      )}
    </>
  )
}

export default Actions
